// Browser-side helpers shared across the bundle builders. All data fetches
// go through the user-authenticated Supabase client, so RLS applies.
//
// Photo / file fetches:
//   - Firebase-Storage-style URLs (legacy): fetched directly via window.fetch
//   - Supabase Storage paths: pulled via supabase.storage.from(bucket).download()

import { supabase } from "@/shared/integrations/supabase/client";
import { sanitize } from "@/features/exports/services/_helpers";
import { getAppOriginUrl } from "@/shared/lib/appBasePath";

export interface PropertyMeta {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  store_number: string | null;
  square_footage: number | null;
}

export async function fetchProperty(propertyId: string): Promise<PropertyMeta | null> {
  const { data, error } = await supabase
    .from("properties")
    .select(
      "id, name, address, city, state, zip_code, store_number, square_footage",
    )
    .eq("id", propertyId)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as PropertyMeta) ?? null;
}

export function propertyFolderLabel(property: PropertyMeta): string {
  return sanitize(
    property.store_number || property.name || property.address || property.id,
  );
}

export interface BundleAssetRow {
  id: string;
  name: string | null;
  description: string | null;
  category_id: string | null;
  asset_categories: { name: string } | { name: string }[] | null;
  manufacturer: string | null;
  model_name: string | null;
  model_number: string | null;
  serial_number: string | null;
  manufacture_date: string | null;
  estimated_age_years: number | null;
  condition_status: string | null;
  category_attributes: Record<string, unknown> | null;
  notes: string | null;
  zone_id: string | null;
  project_zones: { name: string } | { name: string }[] | null;
  nameplate_photo_url: string | null;
  asset_photo_url: string | null;
}

export async function fetchAssetsForProperty(propertyId: string): Promise<BundleAssetRow[]> {
  const { data, error } = await supabase
    .from("property_assets")
    .select(
      "id, name, description, category_id, asset_categories(name), manufacturer, " +
      "model_name, model_number, serial_number, manufacture_date:manufacture_date_string, estimated_age_years, " +
      "condition_status, category_attributes, notes, zone_id, project_zones(name), " +
      "nameplate_photo_url, asset_photo_url",
    )
    .eq("property_id", propertyId);
  if (error) throw error;
  return (data ?? []) as unknown as BundleAssetRow[];
}

export async function fetchAssetPhotos(assetIds: string[]) {
  if (assetIds.length === 0) return [];
  const PAGE = 500;
  const out: Array<{
    id: string;
    asset_id: string;
    file_path: string | null;
    document_name: string | null;
    category: string | null;
  }> = [];
  for (let i = 0; i < assetIds.length; i += PAGE) {
    const chunk = assetIds.slice(i, i + PAGE);
    const { data, error } = await supabase
      .from("asset_documents")
      .select("id, asset_id, file_path, document_name, category")
      .is("deleted_at", null)
      .eq("document_type", "photo")
      .in("asset_id", chunk);
    if (error) throw error;
    if (data) out.push(...data);
  }
  return out;
}

export async function fetchPropertyFiles(propertyId: string) {
  // Digital-twin files live in the `documents` table — Firebase-sync targets
  // pdf / autodesk / pointCloud / site reports / existing documents / proposal
  // categories. documents.file_path holds the full download URL.
  const { data, error } = await supabase
    .from("documents")
    .select("id, document_name, file_path, category, file_size")
    .eq("property_id", propertyId);

  if (error) throw error;

  return (data ?? []).map(d => ({
    id: d.id,
    filename: d.document_name as string | null,
    file_path: d.file_path as string | null,
    file_category: d.category as string | null,
    file_size: d.file_size as number | null,
    download_url: null as string | null,
  }));
}

// Fetch raw bytes for a photo/file. Supports both Supabase Storage paths
// (uses authenticated client) and legacy Firebase-Storage URLs (HTTP fetch).
export async function fetchBytes(
  source: string,
  options: { bucket?: string; signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<ArrayBuffer | null> {
  const { bucket, signal, timeoutMs = 30_000 } = options;

  try {
    if (source.startsWith("http://") || source.startsWith("https://")) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const onParentAbort = () => controller.abort();
      signal?.addEventListener("abort", onParentAbort);
      try {
        const resp = await fetch(source, { signal: controller.signal });
        if (!resp.ok) return null;
        return await resp.arrayBuffer();
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", onParentAbort);
      }
    }
    if (!bucket) return null;
    const { data, error } = await supabase.storage.from(bucket).download(source);
    if (error || !data) return null;
    return await data.arrayBuffer();
  } catch {
    return null;
  }
}

// Run async tasks with bounded concurrency. Throwing tasks are swallowed —
// they should record their own failure status. Honors an external AbortSignal.
export async function runWithConcurrency(
  tasks: Array<() => Promise<void>>,
  limit: number,
  signal?: AbortSignal,
): Promise<void> {
  let cursor = 0;
  const next = async (): Promise<void> => {
    while (cursor < tasks.length) {
      if (signal?.aborted) return;
      const idx = cursor++;
      try {
        await tasks[idx]();
      } catch (err) {
        console.error("download task failed:", err);
      }
    }
  };
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => next());
  await Promise.all(workers);
}

export function csvCell(value: unknown): string {
  if (value == null) return "";
  const s = String(value);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function calcAge(dateStr: string | null | undefined): string {
  if (!dateStr) return "Unknown";
  try {
    const d = new Date(dateStr);
    const years = (Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    return years.toFixed(1);
  } catch {
    return "Unknown";
  }
}

export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export function getAppBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return getAppOriginUrl();
  }
  return "";
}
